import { useState, useMemo } from 'react'
import classnames from 'classnames'
import ImagePreview from '../../components/image-preview'
import Image from '../../components/image'
import { issueTypes } from './inspection'

const initialPhotoPreviewState = { open: false, imageFileKey: null }

export default function IssueDetails({
  description,
  conditions,
  hasLookedAtByTechnician,
  repairEstimate,
}) {
  const [photoPreviewState, setPhotoPreviewState] = useState(initialPhotoPreviewState)

  const images = useMemo(() => {
    if (conditions[0].type === issueTypes.image) {
      return conditions.map((condition) => ({ url: condition.image, id: condition.image }))
    }
  }, [conditions])

  const handlePreviewClose = () => setPhotoPreviewState(initialPhotoPreviewState)
  const handleImageClick = (image) => {
    setPhotoPreviewState({
      open: true,
      src: images.find((entry) => entry.url === image).url,
    })
  }

  return (
    <div className='wrapper flex'>
      <div className='label'>{description}</div>
      <div
        className={classnames('conditions', {
          mgBottom: conditions[0].type === issueTypes.serviceLights && repairEstimate,
        })}
      >
        {conditions.map(({ type, ...condition }, index) => {
          switch (type) {
            case issueTypes.text:
              return (
                <div className='flex alignCenter justifyStart' key={`${condition.label}-${index}`}>
                  {!condition.issueDescription && <span className='icon'>{condition.icon}</span>}
                  {!!condition.issueDescription ? (
                    <div>
                      <div className='flex alignCenter'>
                        <span className='icon flex alignCenter'>{condition.icon}</span>
                        <p> {condition.issueDescription}</p>
                      </div>
                      {hasLookedAtByTechnician && (
                        <p className='estimateRepair'>
                          Reviewed by technician <br />
                          Estimated repair ${repairEstimate}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p>{condition.label}</p>
                  )}
                </div>
              )
            case issueTypes.image:
              return (
                <div className='issueDetails flex alignCenter' key={condition.image}>
                  <button onClick={() => handleImageClick(condition.image)}>
                    <Image src={condition.image} alt='issue' width='43' height='43' />
                  </button>
                  {!!condition.issueDescription ? (
                    <p>{condition.issueDescription}</p>
                  ) : (
                    <p>{condition.label}</p>
                  )}
                </div>
              )
            case issueTypes.serviceLights:
              return (
                <div className='serviceLight' key={`${condition.label}-${index}`}>
                  <div className='option flex alignCenter'>
                    <span className='icon absoluteCenter'>{condition.icon}</span>
                    {!!condition.issueDescription ? (
                      <span>{condition.issueDescription}</span>
                    ) : (
                      <span>{condition.label}</span>
                    )}
                    {hasLookedAtByTechnician && index === 0 && (
                      <span className='estimateOfServiceLights'>
                        Reviewed by technician <br />
                        Estimated repair ${repairEstimate}
                      </span>
                    )}
                  </div>
                </div>
              )
            default:
              return null
          }
        })}
      </div>

      {photoPreviewState.open && (
        <ImagePreview onClose={handlePreviewClose} src={photoPreviewState.src} />
      )}

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 100%;
        }

        .label {
          padding: 0 5rem 0 0;
          flex: 1;
          font-size: 1.8rem;
        }

        .conditions {
          flex: 2;

          .icon {
            width: 2.03rem;
            margin: 0 2rem 0 0;
          }

          p {
            font-weight: 700;
            font-size: 1.8rem;
            line-height: 2.4rem;
          }
        }

        .issueDetails {
          button {
            width: 4.3rem;
            height: 4.3rem;
            margin: 0 2rem 0 -2rem;
            background: var(--secondary-input-bg);
            border-radius: 50%;
            overflow: hidden;

            :global(img) {
              object-fit: cover;
            }
          }
        }

        .issueDetails + .issueDetails {
          margin: 1.5rem 0 0;
        }

        .serviceLight {
          .option {
            height: 4rem;
            border-radius: 2.1rem;
            font-size: 1.8rem;
            font-weight: 700;
          }

          .icon {
            display: inline-block;
            width: 2rem;
            height: 2rem;
            margin: 0 2rem 0 0;
            border-radius: 50%;
            background: var(--alert-color);

            :global(svg) {
              max-width: 70%;
              max-height: 70%;
            }
          }
        }

        .mgBottom {
          margin: 0 0 5rem;
        }

        .estimateRepair {
          margin: 1rem 0 0 4.03rem;
        }

        .estimateOfServiceLights {
          position: absolute;
          bottom: 0;
          padding: 0 0 0 4rem;
          color: var(--primary-text-color);
        }

        @media (min-width: 1025px) and (max-width: 1364px) {
          .label {
            padding: 0 5rem 0 0;
            flex: 1;
            font-size: 1.4rem;
          }

          .conditions {
            .icon {
              width: 1.5rem;
              margin: 0 1.5rem 0 0;
            }

            p {
              font-size: 1.4rem;
              line-height: 1.9rem;
            }
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            flex-direction: column;
          }

          .label {
            width: 100%;
            padding: 0 0 1.2rem;
            color: #a5a8b9;
            font-size: 1.4rem;
          }

          .conditions {
            &.grid {
              grid-template-columns: repeat(auto-fill, 11rem);
            }

            .icon {
              margin: 0 2rem 0 0.5rem;
            }

            p {
              font-size: 1.4rem;
            }

            .estimateRepair {
              margin: 0.6rem 0 0 4.53rem;
              line-height: 1.7rem;
            }
          }

          .issueDetails {
            button {
              margin: 0 2rem 0 0;
            }
          }

          .serviceLight {
            .option {
              height: 3.5rem;
              padding: 0 0 0 0.5rem;
              border-radius: 1.8rem;
              font-size: 1.4rem;
            }

            .icon {
              width: 2rem;
              height: 2rem;
              margin: 0;
            }

            span {
              margin: 0 0 0 2rem;
            }
          }

          .mgBottom {
            margin: 0 0 3.8rem;
          }

          .estimateOfServiceLights {
            padding: 0 0 0 2rem;
          }
        }
      `}</style>
    </div>
  )
}
